import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import type { WorkOrderChangePriceData, WorkOrderChangeSeasonData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';

import type { ColDef, GridApi } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useMemo } from 'react';

import {
    WorkOrderChangeIsEditableCheck,
    WorkOrderChangeSeasonValueGetter,
    WorkOrderChangeSeasonValueRenderer,
    WorkOrderChangeSeasonVolumeEditRenderer,
} from '../../CellRenderers';
import { SEASON_COST_COLUMNS_WIDTH, SEASON_VOLUME_COLUMNS_WIDTH } from '../../constants';

interface IUseWorkOrderChangeSeasonsColumns {
    workOrderChangeId: number;
    isActionsDisabled: boolean;
    api: GridApi;
    constructionTypeId: number;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
    refreshTableObjectsProp?: (objectIds: number[], needUpdateOnlyObj: boolean) => void;
}

export const useWorkOrderChangeSeasonsColumns = (props: IUseWorkOrderChangeSeasonsColumns) => {
    const {
        workOrderChangeId,
        isActionsDisabled,
        api,
        workOrderChangeUerRules,
        costRounding,
        workOrderChangeSeasons,
        constructionTypeId,
        refreshTableObjectsProp,
    } = props;

    const getVariablesChangeVolume = useCallback(
        (data: WorkOrderChangePriceData, workOrderChangeSeasonId: number, value: number) => ({
            // @ts-ignore
            ...R.pickAll(['workOrderPriceId', 'workOrderChangePriceId'], data),
            workOrderChangeSeasonId,
            workOrderChangeId,
            volume: value,
        }),
        [workOrderChangeId],
    );

    return useMemo(
        () =>
            workOrderChangeSeasons.reduce(
                (acc: { columns: ColDef[]; isOdd: boolean }, item: WorkOrderChangeSeasonData) => {
                    const cellClass = acc.isOdd ? 'oddSeason' : 'evenSeason';

                    const season = [
                        {
                            headerName: `Сезон ${item.year}, план`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `planVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonId: item.id,
                                        seasonYear: item.year,
                                        dataKey: 'planVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonVolumeEditRenderer,
                                    cellRendererParams: {
                                        refreshTableObjectsProp,
                                        seasonId: item.id,
                                        apiGrid: api,
                                        getVariablesChangeVolume,
                                        checkIsEditable: WorkOrderChangeIsEditableCheck(isActionsDisabled),
                                        workOrderChangeUerRules,
                                        workOrderChangeId,
                                        constructionTypeId,
                                        maxDecimal: 6,
                                    },
                                    cellClass,
                                    suppressKeyboardEvent: suppressKeyboardEventForInput,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'planCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: costRounding,
                                        ignoreOtherGroup: true,
                                    }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, выполнено`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `completedVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'completedVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: 3,
                                        maxDecimal: 6,
                                        withDeviation: true,
                                        ignoreOther: true,
                                        ignoreRoot: true,
                                    }),
                                    cellClass,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'completedCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: costRounding,
                                        ignoreOtherGroup: true,
                                    }),
                                    cellClass,
                                },
                            ],
                        },
                        {
                            headerName: `Сезон ${item.year}, остаток`,
                            marryChildren: true,
                            children: [
                                {
                                    headerName: 'Объем',
                                    field: `remainderVolume_${item.year}`,
                                    width: SEASON_VOLUME_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'remainderVolume',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: 3,
                                        maxDecimal: 6,
                                        ignoreOther: true,
                                        ignoreRoot: true,
                                    }),
                                    cellClass,
                                },
                                {
                                    headerName: 'Стоимость, руб. без НДС',
                                    width: SEASON_COST_COLUMNS_WIDTH,
                                    valueGetter: WorkOrderChangeSeasonValueGetter({
                                        seasonYear: item.year,
                                        seasonId: item.id,
                                        dataKey: 'remainderCost',
                                    }),
                                    cellRenderer: WorkOrderChangeSeasonValueRenderer({
                                        decimal: costRounding,
                                        ignoreOtherGroup: true,
                                    }),
                                    cellClass,
                                },
                            ],
                        },
                    ];

                    acc.columns.push(...season);
                    acc.isOdd = !acc.isOdd;

                    return acc;
                },
                {
                    columns: [],
                    isOdd: true,
                },
            ).columns,
        [
            workOrderChangeSeasons,
            refreshTableObjectsProp,
            api,
            getVariablesChangeVolume,
            isActionsDisabled,
            workOrderChangeUerRules,
            workOrderChangeId,
            constructionTypeId,
            costRounding,
        ],
    );
};
